import { ApolloClient } from '@apollo/client'
import {
  catchError,
  defer,
  from,
  lastValueFrom,
  map,
  Observable,
  of,
  startWith,
  switchMap,
  timer,
} from 'rxjs'

import {
  CartFragment,
  ShopPayConfirmationDocument,
  ShopPayConfirmationStatus,
  ShopPayPaymentInFlightDocument,
  ShopPaySessionStartOverDocument,
} from '../../graphql/magento'
import { logAndCaptureException, logAndCaptureMessage } from '../../utils/errorTools'
import { sessionOrFallbackStorage } from '../../utils/storage'
import { isShopPayCartLockedError } from '../m2/errors'

// After an accepted confirm the shopper holds a reserved order number but no order: sp-relay
// captures the payment and has Magento place it. The confirming step polls for that answer (DFH-77).

const snapshotKey = 'dfh.shopPay.confirmation'

export interface ShopPayConfirmationSnapshot {
  sourceIdentifier: string
  cart: CartFragment
  isGuest: boolean
}

export const saveShopPayConfirmationSnapshot = (snapshot: ShopPayConfirmationSnapshot): void => {
  try {
    sessionOrFallbackStorage.setItem(snapshotKey, JSON.stringify(snapshot))
  } catch {
    // storage is best effort; without it the confirming step cannot poll and says so
  }
}

export const loadShopPayConfirmationSnapshot = (): ShopPayConfirmationSnapshot | null => {
  try {
    const raw = sessionOrFallbackStorage.getItem(snapshotKey)
    return raw ? (JSON.parse(raw) as ShopPayConfirmationSnapshot) : null
  } catch {
    return null
  }
}

export const clearShopPayConfirmationSnapshot = (): void => {
  try {
    sessionOrFallbackStorage.removeItem(snapshotKey)
  } catch {
    // ignore
  }
}

export type ShopPayConfirmationState =
  | { _tag: 'confirming' }
  | { _tag: 'placed'; orderNumber: string }
  | {
      _tag: 'ended'
      status: Exclude<ShopPayConfirmationStatus, 'PENDING' | 'PLACED' | 'NOT_FOUND'>
      copyCartId?: string | null
    }
  // past the ceiling the payment may still complete; Magento emails the shopper when it does
  | { _tag: 'still_processing' }

/**
 * Wait at most `ms` for `promise`, then give up with undefined. A promise that rejects also gives
 * undefined, so the caller always has something to go on.
 */
export const settleWithin = <T>(
  promise: Promise<T> | undefined,
  ms: number,
): Promise<T | undefined> =>
  promise
    ? Promise.race([
        promise.catch(() => undefined),
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms)),
      ])
    : Promise.resolve(undefined)

export const shopPayConfirmationPollDelaysMs = (attempt: number): number =>
  Math.min(1_000 * (attempt + 1), 5_000)

export const shopPayConfirmationCeilingMs = 90_000

type ConfirmationClient = Pick<ApolloClient<object>, 'query'>

/**
 * Poll the confirmation query until Magento gives a final answer or the ceiling passes. A network
 * error or an empty answer is retried. NOT_FOUND right after paying usually means sp-relay has not
 * recorded the payment yet, so it keeps polling rather than telling the shopper nothing was paid.
 */
export const pollShopPayConfirmation = (
  client: ConfirmationClient,
  sourceIdentifier: string,
  now: () => number = Date.now,
): Observable<ShopPayConfirmationState> => {
  const deadline = now() + shopPayConfirmationCeilingMs

  const poll = (attempt: number): Observable<ShopPayConfirmationState> =>
    defer(() =>
      from(
        client.query({
          query: ShopPayConfirmationDocument,
          variables: { sourceIdentifier },
          fetchPolicy: 'no-cache',
          // a customer's confirmation needs their login; a guest's needs only the identifier
          context: { useLatestTokenIfLoggedIn: true },
        }),
      ),
    ).pipe(
      map(({ data }) => data?.shopPayConfirmation ?? null),
      catchError((error: unknown) => {
        logAndCaptureException(error)
        return of(null)
      }),
      switchMap((answer): Observable<ShopPayConfirmationState> => {
        if (!answer || answer.status === 'PENDING' || answer.status === 'NOT_FOUND') {
          return retry(attempt)
        }
        if (answer.status === 'PLACED') {
          return answer.order
            ? of({ _tag: 'placed', orderNumber: answer.order.order_number })
            : retry(attempt)
        }
        return of({ _tag: 'ended', status: answer.status, copyCartId: answer.copy_cart_id })
      }),
    )

  const retry = (attempt: number): Observable<ShopPayConfirmationState> =>
    now() >= deadline
      ? of({ _tag: 'still_processing' })
      : timer(shopPayConfirmationPollDelaysMs(attempt)).pipe(switchMap(() => poll(attempt + 1)))

  return poll(0).pipe(startWith<ShopPayConfirmationState>({ _tag: 'confirming' }))
}

// A payment that never settles leaves the cart locked. Past Magento's minimum duration the page
// offers to start over: Magento retires the cart and hands back a copy to check out with
// (DFH-119, DFH-122).

type StartOverClient = Pick<ApolloClient<object>, 'query' | 'mutate'>

export interface ShopPayPaymentInFlight {
  sourceIdentifier: string
  /** Epoch ms after which Magento allows a start-over */
  minimumDurationEnd: number
}

/** What is in flight on the cart, or null when nothing is (or the read fails). */
export const readShopPayPaymentInFlight = async (
  client: Pick<ApolloClient<object>, 'query'>,
  cartId: string,
): Promise<ShopPayPaymentInFlight | null> => {
  try {
    const { data } = await client.query({
      query: ShopPayPaymentInFlightDocument,
      variables: { cartId },
      fetchPolicy: 'no-cache',
      context: { useLatestTokenIfLoggedIn: true },
    })
    const inFlight = data?.cart?.shop_pay_payment_in_flight
    if (!inFlight) return null
    const minimumDurationEnd = Date.parse(inFlight.minimum_duration_end)
    if (Number.isNaN(minimumDurationEnd)) return null
    return { sourceIdentifier: inFlight.source_identifier, minimumDurationEnd }
  } catch (error) {
    logAndCaptureException(error)
    return null
  }
}

export const shopPayStartOverRefusedMessage =
  "We couldn't start over just yet. Please try again in a moment."

export type ShopPayStartOverResult =
  | { _tag: 'copied'; copyCartId: string }
  | { _tag: 'refused'; message: string }

/** Retire the cart and name its copy. Magento refuses inside the minimum duration. */
export const startOverShopPay = async (
  client: Pick<ApolloClient<object>, 'mutate'>,
  cartId: string,
): Promise<ShopPayStartOverResult> => {
  const result = await client.mutate({
    mutation: ShopPaySessionStartOverDocument,
    variables: { cartId },
    context: { useLatestTokenIfLoggedIn: true },
    errorPolicy: 'all',
  })
  // inside the minimum duration Magento refuses with the cart-locked prefix; any refusal reads the
  // same to the shopper, never as Magento's text
  const message = result.errors?.[0]?.message
  if (message) {
    if (!isShopPayCartLockedError(message)) logAndCaptureMessage(`Shop Pay start over: ${message}`)
    return { _tag: 'refused', message: shopPayStartOverRefusedMessage }
  }
  const copyCartId = result.data?.shopPaySessionStartOver?.copy_cart_id
  return copyCartId
    ? { _tag: 'copied', copyCartId }
    : { _tag: 'refused', message: shopPayStartOverRefusedMessage }
}

/**
 * The copy of a retired cart, read from the session's confirmation answer; null while the session
 * is still pending, once it placed, or when nothing was copied. A guest whose start-over response
 * was lost finds the copy this way instead of recreating an empty cart (DFH-122).
 */
export const recoverShopPayCopyCart = async (
  client: StartOverClient,
  sourceIdentifier: string,
): Promise<string | null> => {
  try {
    const { data } = await client.query({
      query: ShopPayConfirmationDocument,
      variables: { sourceIdentifier },
      fetchPolicy: 'no-cache',
      context: { useLatestTokenIfLoggedIn: true },
    })
    return data?.shopPayConfirmation?.copy_cart_id ?? null
  } catch (error) {
    logAndCaptureException(error)
    return null
  }
}

export type ShopPayCartUnlocked =
  /** The source cart placed its order; there is nothing left to merge */
  | { _tag: 'placed' }
  /** The cart was retired and this copy holds its items */
  | { _tag: 'copied'; copyCartId: string }
  /** The attempt failed and the same cart is usable again */
  | { _tag: 'unlocked' }

// How long a deferred merge keeps watching before trying the merge again regardless.
export const shopPayUnlockWaitCeilingMs = 30 * 60_000

/**
 * Wait for the Shop Pay payment on `cartId` to leave Submitted, for a cart merge refused while it
 * is being paid for (DFH-122). The session is read off the cart, then its confirmation is polled.
 */
export const waitForShopPayCartToUnlock = async (
  client: StartOverClient,
  cartId: string,
  now: () => number = Date.now,
): Promise<ShopPayCartUnlocked> => {
  const inFlight = await readShopPayPaymentInFlight(client, cartId)
  if (!inFlight) return { _tag: 'unlocked' }
  const deadline = now() + shopPayUnlockWaitCeilingMs
  for (;;) {
    const state = await lastValueFrom(
      pollShopPayConfirmation(client, inFlight.sourceIdentifier, now),
    )
    if (state._tag === 'placed') return { _tag: 'placed' }
    if (state._tag === 'ended') {
      return state.copyCartId
        ? { _tag: 'copied', copyCartId: state.copyCartId }
        : { _tag: 'unlocked' }
    }
    if (now() >= deadline) return { _tag: 'unlocked' }
  }
}
